/**
 * Renders an in-bubble A/B/C choice widget for a validation-resolver
 * directive. The agent emits a [RESOLVER_OPTIONS] tag inside an
 * assistant message (parsed by MessageBubble.extractResolverOptions);
 * this component handles the round-trip back to the agent via the
 * chatBridgeStore's `resolverSend`.
 *
 * Option C carries a free-text input — the user types an override and
 * the agent obeys verbatim.
 *
 * Disabled when WS is not wired (`resolverSend === null`), when the user
 * already answered this context in the current session, OR when the
 * /ws/chat agent loop is mid-turn (`isAgentActive`) — preventing a
 * double-fire on top of an in-flight response. See frontend-doctrine §4.3.
 */

import { useState } from "react";

import { useChatBridgeStore } from "@/store/chatBridgeStore";

export interface ResolverOptionsPayload {
  readonly context: string;
  readonly optionA: { readonly label: string };
  readonly optionB: { readonly label: string };
  readonly optionC: { readonly placeholder: string };
}

interface ResolverChoiceProps {
  readonly options: ResolverOptionsPayload;
}

export function ResolverChoice({ options }: ResolverChoiceProps) {
  const resolverSend = useChatBridgeStore((s) => s.resolverSend);
  const markResolved = useChatBridgeStore((s) => s.markResolved);
  const isResolved = useChatBridgeStore((s) =>
    s.resolvedContexts.has(options.context),
  );
  const isAgentActive = useChatBridgeStore((s) => s.isAgentActive);
  const [userText, setUserText] = useState("");

  const disabled = resolverSend === null || isResolved || isAgentActive;

  const handleChoice = (choice: "A" | "B" | "C") => {
    if (disabled || resolverSend === null) return;
    if (choice === "C" && userText.trim().length === 0) return;
    markResolved(options.context);
    resolverSend({
      context: options.context,
      choice,
      ...(choice === "C" ? { userText: userText.trim() } : {}),
    });
  };

  return (
    <div
      data-testid="resolver-choice"
      data-context={options.context}
      className="mt-3 flex flex-col gap-2 rounded-lg border border-brand-coral/30 bg-brand-coral/5 p-3 not-prose"
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => handleChoice("A")}
        className="rounded-md border border-brand-coral/40 bg-white px-3 py-2 text-left text-sm font-semibold text-black transition-colors hover:bg-brand-coral/10 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/5 dark:text-white"
      >
        <span className="mr-2 font-mono text-brand-coral">A.</span>
        {options.optionA.label}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => handleChoice("B")}
        className="rounded-md border border-brand-coral/40 bg-white px-3 py-2 text-left text-sm font-semibold text-black transition-colors hover:bg-brand-coral/10 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/5 dark:text-white"
      >
        <span className="mr-2 font-mono text-brand-coral">B.</span>
        {options.optionB.label}
      </button>
      <div className="flex items-stretch gap-2 rounded-md border border-brand-coral/40 bg-white p-1 dark:bg-white/5">
        <span className="flex items-center pl-2 font-mono text-sm text-brand-coral">
          C.
        </span>
        <input
          type="text"
          value={userText}
          onChange={(e) => setUserText(e.target.value)}
          disabled={disabled}
          placeholder={options.optionC.placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleChoice("C");
          }}
          className="flex-1 bg-transparent px-1 py-1 text-sm text-black outline-none placeholder:text-black/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:placeholder:text-white/40"
        />
        <button
          type="button"
          disabled={disabled || userText.trim().length === 0}
          onClick={() => handleChoice("C")}
          className="rounded bg-brand-coral px-3 py-1 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-coral/80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send
        </button>
      </div>
      {isResolved ? (
        <span className="text-[10px] uppercase tracking-wide text-black/50 dark:text-white/50">
          Answered
        </span>
      ) : null}
    </div>
  );
}
